import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { useMutation } from '@zenbusiness/application-commons-graphql';
import { WebView } from '@zenbusiness/application-commons-webview';
import Banner from '@zenbusiness/zui/Banner';
import SharedAsset from '@zenbusiness/zui/SharedAsset';

import { DB_APPLICATION_STATUS } from '#client/constants/application';
import useApplicationStatusContext from '#client/hooks/useApplicationStatusContext';
import useBankingPath from '#client/hooks/useBankingPath';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { ENABLE_MFA_MUTATION } from '#client/mutations';
import { BannerWrapperStyled } from '#client/pages/SharedStyles';

export default function UnAuthenticatedBanner() {
  const { isWebView } = WebView;
  const { isValidMfa, isEnabled, isEnrolled, isFetchingMfaStatus, mfaError } = useUnitAccountId();
  const { appStatus, loading: loadingAppStatus } = useApplicationStatusContext();
  const [loading, setLoading] = useState(false);
  const { login } = useAuthentication();
  const { createCommonErrorToast } = useToastContext();
  const [enableMFA] = useMutation(ENABLE_MFA_MUTATION, {
    noThrow: true
  });
  const bankingPath = useBankingPath();

  const redirectToMFA = useCallback(() => login(`${bankingPath}/dashboard`), [login, bankingPath]);

  const isUnauthenticated = useMemo(() => {
    if (
      !!mfaError ||
      (appStatus && appStatus === DB_APPLICATION_STATUS.NOT_STARTED && !isEnabled)
    ) {
      return false;
    }
    return !isValidMfa || !isEnabled || !isEnrolled;
  }, [appStatus, isEnabled, isEnrolled, isValidMfa, mfaError]);

  const isShowBanner = isUnauthenticated && !isWebView;

  const handleLogout = useCallback(async () => {
    setLoading(true);
    if (!isEnabled) {
      const { error } = await enableMFA();

      if (error) {
        setLoading(false);
        createCommonErrorToast({ error });
        return;
      }
    }

    redirectToMFA();
  }, [createCommonErrorToast, enableMFA, isEnabled, redirectToMFA]);

  useEffect(() => {
    if (!loading && !isFetchingMfaStatus && isUnauthenticated && isWebView) {
      handleLogout();
    }
  }, [handleLogout, isFetchingMfaStatus, isUnauthenticated, loading, isWebView]);

  if (loadingAppStatus || isFetchingMfaStatus) {
    return undefined;
  }
  return isShowBanner ? (
    <BannerWrapperStyled>
      <Banner
        slotProps={{
          primaryAction: {
            label: 'Verify account',
            disabled: loading,
            loading,
            onClick: handleLogout
          }
        }}
        slots={{
          startIcon: (
            <SharedAsset
              alt=""
              src="/icon/micro/alert-fill.svg"
            />
          )
        }}
        subtitle="Please log out, then sign back in completing the two-factor authentication for security. If you have issues, contact customer support."
        sx={(theme) => ({ backgroundColor: theme.palette.background.grey.light })}
        title="Verify your account for security"
      />
    </BannerWrapperStyled>
  ) : null;
}
